import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const { setAuthenticated } = useAuth();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Helper to safely navigate with error message (ensures serializable state)
      const navigateWithError = (errorMessage) => {
        // Ensure error message is a plain string (serializable for postMessage)
        const safeErrorMessage = typeof errorMessage === 'string' 
          ? errorMessage 
          : (errorMessage?.toString?.() || 'Authentication failed');
        
        navigate('/', { 
          replace: true, 
          state: { error: safeErrorMessage } 
        });
      };

      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        console.log('Auth callback - processing URL hash');
        
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          console.error('No session_id found in URL');
          navigateWithError('No session ID received from Google');
          return;
        }

        const sessionId = sessionIdMatch[1];
        console.log('Got session_id, exchanging via backend...');

        // Exchange session_id via our backend (avoids CORS issues)
        // Retry logic for cold start scenarios
        let response;
        let lastError;
        const maxRetries = 3;
        
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            console.log(`Auth attempt ${attempt}/${maxRetries}...`);
            response = await fetch(`${BACKEND_URL}/api/auth/exchange-session`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              credentials: 'include',
              body: JSON.stringify({ session_id: sessionId }),
            });
            // If we get here, request succeeded
            break;
          } catch (fetchError) {
            console.error(`Network error on attempt ${attempt}:`, fetchError);
            lastError = fetchError;
            
            if (attempt < maxRetries) {
              // Wait before retry (exponential backoff: 1s, 2s, 4s)
              const delay = Math.pow(2, attempt - 1) * 1000;
              console.log(`Retrying in ${delay}ms...`);
              await new Promise(resolve => setTimeout(resolve, delay));
            }
          }
        }
        
        if (!response) {
          console.error('All auth attempts failed');
          navigateWithError('Network error: Unable to connect to server. Please try again.');
          return;
        }

        console.log('Backend response status:', response.status);

        if (!response.ok) {
          let errorMessage = 'Authentication failed';
          try {
            const errorData = await response.json();
            // Extract only the string message, not the whole object
            errorMessage = typeof errorData.detail === 'string' 
              ? errorData.detail 
              : (errorData.message || errorData.error || 'Authentication failed');
          } catch (e) {
            // JSON parse error - use status text
            errorMessage = `Authentication failed (${response.status})`;
          }
          
          console.error('Auth error:', errorMessage);
          navigateWithError(errorMessage);
          return;
        }

        const userData = await response.json();
        console.log('Session created for:', userData.email);
        
        // IMPORTANT: Update the AuthContext state directly before navigating
        // This prevents the race condition where ProtectedRoute checks auth
        // before the context is updated
        setAuthenticated(userData);
        
        // Small delay to ensure state is propagated
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Success - redirect to dashboard
        navigate('/dashboard', { replace: true });
        
      } catch (error) {
        console.error('Auth callback error:', error);
        // IMPORTANT: Extract only the message string, not the error object
        // This prevents "Request object could not be cloned" error
        const errorMsg = typeof error === 'string' 
          ? error 
          : (error?.message || 'Unknown error during authentication');
        navigateWithError(errorMsg);
      }
    };

    processAuth();
  }, [navigate, location, BACKEND_URL, setAuthenticated]);

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400 mx-auto mb-4"></div>
        <p className="text-gray-400">Signing you in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
